import { Stack } from "@mui/system";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import STUnclassifiedFilterItems from "./STUnclassifiedFilterItems";
import OtherKeywordST from "./OtherKeywordST";

import {
  setDefinitionModalSourceForUnclassifiedST,
  setDefinitionModalTypeForUnclassifiedST,
  setIsDefinitionModalOpenForUnclassifiedST,
  setIsOpenOtherKeywordDialogSTOUnclassified,
  setOtherKeywordsForUnclassifiedST,
  setSelectedRowsForUnclassifiedST,
  setSelectedSearchTermForUnclassifiedST,
} from "../../../../../../../../../features/searchTermTabSlice";
import DefinitionModalST from "./DefinitionModalST";
import { selectUserData } from "../../../../../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useSearchParams } from "react-router-dom";

const icons = [
  {
    label: "Exclusive",
    src: "/Elock.svg",
    coloredSrc: "/EclockColored.svg",
    alt: "Exclusive lock",
    tooltip: "Set as Exclusive",
    dataTracker: "539",
  },
  {
    label: "Allowed",
    src: "/Aright.svg",
    coloredSrc: "/ArightColored.svg",
    alt: "Allowed check",
    tooltip: "Set as Allowed",
    dataTracker: "541",
  },
  {
    label: "Negative",
    src: "/Ncross.svg",
    coloredSrc: "/NcrossColored.svg",
    alt: "Negative cross",
    tooltip: "Set as Negative",
    dataTracker: "540",
  },
];

function STUnclassifiedFilterCard() {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasCreateSearchTermTabPermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "plan_campaign_ppc",
    "create_st_control",
  );

  const {
    stWithPerformanceUnClassified,
    selectedSearchTermForUnclassifiedST,
    selectedRowsForUnclassifiedST,
    isDefinitionModalOpenForUnclassifiedST,
    isOpenOtherKeywordDialogSTOUnclassified,
  } = useSelector((state: any) => state.searchTermTab);

  //--- Function to handle close of Other Keywords Dialog
  const handleCloseTheOtherKeywordDialog = () => {
    dispatch(setOtherKeywordsForUnclassifiedST([])); // Clear other keywords data
    dispatch(setIsOpenOtherKeywordDialogSTOUnclassified(false)); // Close the modal
    //  dispatch(setSelectedDefinitionsForUnclassifiedST({})); // Clear selected definitions
    //  dispatch(setDefinitionModalSourceForUnclassifiedST("")); // Clear source (headerIcon or saveButton)
  };

  //-- Function to handle close of Save Changes Dialog
  const handleCloseDefinitionModalForUnclassifiedST = () => {
    dispatch(setIsDefinitionModalOpenForUnclassifiedST(false)); // Close the modal
    dispatch(setDefinitionModalSourceForUnclassifiedST("")); // Clear source (headerIcon or saveButton)
    //dispatch(setSelectedRowsForUnclassifiedST([])); // Clear selected rows
    //dispatch(setSelectedDefinitionsForUnclassifiedST({})); // Clear selected definitions
  };

  const clearSelectedSearchTerm = () => {
    dispatch(setSelectedSearchTermForUnclassifiedST(""));
  };

  const searchOptions = useMemo(() => {
    const set = new Set<string>();
    (stWithPerformanceUnClassified || []).forEach((r: any) => {
      if (typeof r?.search_term === "string") set.add(r.search_term.trim());
    });
    return Array.from(set); // ["shoes", "hats", ...]
  }, [stWithPerformanceUnClassified]);

  const handleClick = (type: string) => {
    dispatch(setIsDefinitionModalOpenForUnclassifiedST(true));
    dispatch(setSelectedRowsForUnclassifiedST(selectedRowsForUnclassifiedST));
    dispatch(setDefinitionModalSourceForUnclassifiedST("headerIcon"));
    dispatch(setDefinitionModalTypeForUnclassifiedST(type));
  };

  // const activeFilter =
  // "invert(13%) sepia(94%) saturate(4247%) hue-rotate(273deg) brightness(55%) contrast(110%)";
  // const inactiveFilter = "grayscale(100%) opacity(0.5)";
  const hasSelection = selectedRowsForUnclassifiedST.length > 0;

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(
      setSelectedSearchTermForUnclassifiedST(searchParams.get("search")),
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ padding: "8px 16px", marginTop: "8px", boxShadow: "none" }}>
        <Stack flexDirection={"row"} justifyContent={"start"} gap={2}>
          <Autocomplete
            fullWidth
            disablePortal
            freeSolo //user can type anything
            data-tracker="536"
            sx={{
              width: "390px",
              // ↓ Always show clear button when searchbox has value
              "& .MuiAutocomplete-clearIndicator": {
                visibility: selectedSearchTermForUnclassifiedST
                  ? "visible"
                  : "hidden",
              },
            }}
            options={searchOptions}
            value={selectedSearchTermForUnclassifiedST || ""}
            disableClearable={!selectedSearchTermForUnclassifiedST} //✕ only when there is a value
            onInputChange={(_, newVal) => {
              //Every keystroke → Redux → shows X button
              dispatch(setSelectedSearchTermForUnclassifiedST(newVal ?? ""));
            }}
            onChange={(_, newValue: string | null) => {
              dispatch(setSelectedSearchTermForUnclassifiedST(newValue ?? ""));
              setSearchParams((prevParams) => {
                const updatedParams = Object.fromEntries(prevParams);
                if (newValue) {
                  updatedParams.search = newValue;
                  updatedParams.page = "0";
                } else {
                  delete updatedParams.search;
                }
                return updatedParams;
              });
            }}
            getOptionLabel={(opt) => opt || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Term"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) {
                      dispatch(setSelectedSearchTermForUnclassifiedST(val));
                      setSearchParams((prevParams) => {
                        const updatedParams = Object.fromEntries(prevParams);
                        updatedParams.search = val;
                        updatedParams.page = "0";
                        return updatedParams;
                      });
                      (e.target as HTMLInputElement).blur();
                    }
                  }
                }}
              />
            )}
            size="small"
          />

          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            <STUnclassifiedFilterItems
              clearSelectedSearchTerm={clearSelectedSearchTerm}
            />
            {/* --- Bulk Actions Header --- */}
            <Stack gap={0.5}>
              {/* Row for Set As icons */}
              <Stack
                flexDirection="row"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {/* Label */}
                  <Typography variant="body1">Set As:</Typography>

                  {icons.map(
                    ({ label, src, coloredSrc, alt, tooltip, dataTracker }) => {
                      const isDisabled =
                        !hasSelection || !hasCreateSearchTermTabPermission;

                      const iconSrc = hasSelection ? coloredSrc : src;

                      return (
                        <span key={label}>
                          <Tooltip
                            title={!isDisabled ? tooltip : ""}
                            arrow
                            disableHoverListener={isDisabled}
                          >
                            <span>
                              <IconButton
                                data-tracker={dataTracker}
                                size="small"
                                disabled={isDisabled}
                                onClick={() => handleClick(label)}
                              >
                                <img
                                  src={iconSrc}
                                  alt={alt}
                                  width={hasSelection ? 24 : 32}
                                  height={hasSelection ? 24 : 32}
                                  // style={{
                                  //   filter: !isDisabled
                                  //     ? activeFilter
                                  //     : inactiveFilter,
                                  // }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </span>
                      );
                    },
                  )}
                </Stack>
              </Stack>

              {/* Message BELOW */}
              <Typography variant="body2" color="text.secondary">
                * To perform bulk actions, select at least one row.
              </Typography>
            </Stack>
          </Stack>

          {/* Dialog for Other Keywords */}
          <OtherKeywordST
            open={isOpenOtherKeywordDialogSTOUnclassified}
            onClose={handleCloseTheOtherKeywordDialog}
          />
          <DefinitionModalST
            open={isDefinitionModalOpenForUnclassifiedST}
            onClose={handleCloseDefinitionModalForUnclassifiedST}
          />
        </Stack>
      </Paper>
    </>
  );
}
export default STUnclassifiedFilterCard;
